import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperAirplaneIcon, SparklesIcon, TrashIcon, PlusIcon, PencilIcon, CheckIcon } from '@heroicons/react/24/solid';
import {
  IChatMessage,
  ChatContextType,
  ChatMessageRole,
  LLMStreamEventType,
  EntityActionCard,
  EntityAIResponse
} from '@packages/global';
import { chatMessageApi, llmApi } from '../../../services/user';
import { useToast } from '../../../stores/toastStore';
import { useChatMessageCacheStore } from '../../../stores/chatMessageCacheStore';

export interface EntityChatPanelProps {
  projectId: string;
  episodeScriptId: string;
  contextType: ChatContextType;
  entityName: string; // 如 "角色"、"道具"、"场景"
  currentData?: { items: Array<{ name: string }> } | null;
  onApplyAction?: (action: EntityActionCard) => void | Promise<void>;
}

export default function EntityChatPanel({
  projectId,
  episodeScriptId,
  contextType,
  entityName,
  currentData,
  onApplyAction
}: EntityChatPanelProps) {
  const { t } = useTranslation(['script', 'common']);
  const toast = useToast();

  const ActionTypeLabels: Record<string, string> = {
    create: t('action.add'),
    update: t('action.edit'),
    delete: t('action.delete')
  };
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 防止并发请求（React StrictMode 双重渲染时第二次请求在第一次完成前发出）
  const loadingRef = useRef(false);

  const loadMessages = useCallback(async () => {
    // 优先从缓存读取
    const cached = useChatMessageCacheStore.getState().getCached(episodeScriptId, contextType);
    if (cached) {
      setMessages(cached);
      return;
    }
    // 防止并发重复请求
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      setLoading(true);
      const data = await chatMessageApi.list({
        projectId: episodeScriptId,
        contextType,
        limit: 50
      });
      setMessages(data);
      useChatMessageCacheStore.getState().setCache(episodeScriptId, contextType, data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [episodeScriptId, contextType]);

  useEffect(() => {
    loadMessages();
    return () => { abortControllerRef.current?.abort(); };
  }, [loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages, streamingContent]);

  const handleSend = async () => {
    if (!input.trim() || streaming) return;
    const userMessage = input.trim();
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setStreaming(true);
    setStreamingContent('');

    const tempUserMsg: IChatMessage = {
      id: `temp-${Date.now()}`,
      projectId: episodeScriptId,
      contextType,
      role: ChatMessageRole.USER,
      content: userMessage,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setMessages(prev => [...prev, tempUserMsg]);
    useChatMessageCacheStore.getState().appendMessage(episodeScriptId, contextType, tempUserMsg);

    try {
      abortControllerRef.current = new AbortController();
      await llmApi.stream(
        {
          projectId: episodeScriptId,
          contextType,
          prompt: userMessage,
          currentContent: JSON.stringify(currentData)
        },
        (event) => {
          switch (event.type) {
            case LLMStreamEventType.DELTA:
              setStreamingContent(prev => prev + (event.content || ''));
              break;
            case LLMStreamEventType.DONE:
              const aiMsg: IChatMessage = {
                id: `ai-${Date.now()}`,
                projectId: episodeScriptId,
                contextType,
                role: ChatMessageRole.ASSISTANT,
                content: event.content || '',
                createdAt: new Date(),
                updatedAt: new Date()
              };
              setMessages(prev => [...prev, aiMsg]);
              useChatMessageCacheStore.getState().appendMessage(episodeScriptId, contextType, aiMsg);
              setStreamingContent('');
              setStreaming(false);
              break;
            case LLMStreamEventType.ERROR:
              toast.error(event.error || t('chatPanel.aiFailed'));
              setStreaming(false);
              setStreamingContent('');
              break;
          }
        },
        abortControllerRef.current.signal
      );
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        toast.error(t('chatPanel.sendFailed'));
      }
      setStreaming(false);
      setStreamingContent('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = async () => {
    if (!confirm(t('chatPanel.clearConfirm'))) return;
    try {
      await chatMessageApi.clear(episodeScriptId, contextType);
      setMessages([]);
      useChatMessageCacheStore.getState().clearCache(episodeScriptId, contextType);
      toast.success(t('chatPanel.cleared'));
    } catch {
      toast.error(t('chatPanel.clearFailed'));
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (messageId.startsWith('temp-') || messageId.startsWith('ai-')) {
      setMessages(prev => prev.filter(m => m.id !== messageId));
      useChatMessageCacheStore.getState().removeMessage(episodeScriptId, contextType, messageId);
      return;
    }
    try {
      await chatMessageApi.delete(messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      useChatMessageCacheStore.getState().removeMessage(episodeScriptId, contextType, messageId);
      toast.success(t('chatPanel.deleted'));
    } catch {
      toast.error(t('chatPanel.deleteFailed'));
    }
  };

  // 应用操作卡片
  const handleApply = async (action: EntityActionCard) => {
    if (!onApplyAction) return;
    try {
      await onApplyAction(action);
      toast.success(t('chatPanel.applied'));
    } catch (err: any) {
      toast.error(err.message || t('chatPanel.applyFailed'));
    }
  };

  // 获取操作按钮配置
  const getActionButtonConfig = (actionType: string) => {
    switch (actionType) {
      case 'create':
        return { icon: PlusIcon, text: t('action.add'), color: '#22C55E' };
      case 'update':
        return { icon: PencilIcon, text: t('chatPanel.apply'), color: '#2563EB' };
      case 'delete':
        return { icon: TrashIcon, text: t('action.delete'), color: '#EF4444' };
      default:
        return { icon: CheckIcon, text: t('chatPanel.apply'), color: '#FF5C00' };
    }
  };

  const formatTime = (date: Date) => {
    const d = new Date(date);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  // 解析消息内容，兼容多种格式
  const parseMessageContent = (content: string): { text: string; actions: EntityActionCard[] } => {
    try {
      // 1. 尝试匹配 markdown 代码块中的 JSON
      const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[1]) as EntityAIResponse;
        return { text: parsed.message, actions: parsed.actions || [] };
      }

      // 2. 尝试直接解析为 JSON
      const trimmed = content.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        const parsed = JSON.parse(trimmed) as EntityAIResponse;
        return { text: parsed.message, actions: parsed.actions || [] };
      }

      // 3. 尝试提取 JSON 对象
      const jsonObjectMatch = content.match(/\{[\s\S]*"message"[\s\S]*\}/);
      if (jsonObjectMatch) {
        const parsed = JSON.parse(jsonObjectMatch[0]) as EntityAIResponse;
        return { text: parsed.message, actions: parsed.actions || [] };
      }

      return { text: content, actions: [] };
    } catch {
      return { text: content, actions: [] };
    }
  };

  return (
    <div className="h-full flex flex-col bg-bg-page rounded-xl border border-border-subtle overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center">
            <SparklesIcon className="w-4 h-4 text-white" />
          </div>
          <span className="text-sm font-semibold text-gray-50">{t('chatPanel.title')}</span>
        </div>
        <div className="flex items-center gap-2">
          {messages.length > 0 && (
            <button onClick={handleClear} className="p-1.5 text-gray-400 hover:text-red-400 transition-colors" title={t('chatPanel.clearChat')}>
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
          <div className="flex items-center gap-1.5 px-2 py-1 bg-success/10 rounded">
            <div className="w-1.5 h-1.5 bg-success rounded-full" />
            <span className="text-[11px] text-success font-medium">{t('chatPanel.online')}</span>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400">
            <span className="text-sm">{t('common:loading')}</span>
          </div>
        ) : (
          <>
            {/* 欢迎消息 */}
            <div className="flex gap-2.5">
              <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                <SparklesIcon className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="bg-bg-elevated rounded-xl px-3 py-2.5 max-w-[85%]">
                <p className="text-[13px] text-gray-200 leading-relaxed">
                  {t('chatPanel.welcomeMessage')}
                </p>
              </div>
            </div>

            {messages.map((msg) => {
              const isUser = msg.role === ChatMessageRole.USER;
              const { text, actions } = isUser ? { text: msg.content, actions: [] } : parseMessageContent(msg.content);

              return (
                <div key={msg.id} className="flex flex-col gap-2">
                  <div className={`flex gap-2.5 ${isUser ? 'justify-end' : ''}`}>
                    {!isUser && (
                      <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                        <SparklesIcon className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    <div className={`flex flex-col gap-1 max-w-[85%] ${isUser ? 'items-end' : ''}`}>
                      <div className={`rounded-xl px-3 py-2.5 ${isUser ? 'bg-primary/20 border border-primary' : 'bg-bg-elevated'}`}>
                        <p className={`text-[13px] leading-relaxed whitespace-pre-wrap break-words ${isUser ? 'text-primary' : 'text-gray-200'}`}>
                          {text.length > 500 ? text.slice(0, 500) + '...' : text}
                        </p>
                      </div>
                      <div className={`flex items-center gap-2 ${isUser ? 'justify-end' : ''}`}>
                        {!isUser && (
                          <button onClick={() => handleDeleteMessage(msg.id)} className="p-0.5 text-gray-500 hover:text-red-400 transition-colors" title={t('chatPanel.deleteMessage')}>
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        )}
                        <span className={`text-[11px] ${isUser ? 'text-primary/50' : 'text-gray-400'}`}>{formatTime(msg.createdAt)}</span>
                        {isUser && (
                          <button onClick={() => handleDeleteMessage(msg.id)} className="p-0.5 text-gray-500 hover:text-red-400 transition-colors" title={t('chatPanel.deleteMessage')}>
                            <TrashIcon className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Cards */}
                  {!isUser && actions.length > 0 && (
                    <div className="flex flex-col gap-2 ml-9">
                      {actions.map((action, idx) => {
                        const config = getActionButtonConfig(action.actionType);
                        const Icon = config.icon;
                        return (
                          <div key={idx} className="rounded-lg border p-3 bg-bg-surface border-border-default">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span
                                  className="px-2 py-0.5 text-[11px] font-semibold text-white rounded"
                                  style={{ backgroundColor: config.color }}
                                >
                                  {ActionTypeLabels[action.actionType]}
                                </span>
                                <span className="text-[13px] font-semibold text-gray-50">
                                  {action.name}
                                </span>
                              </div>
                              <button
                                onClick={() => handleApply(action)}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors hover:bg-white/5"
                                style={{ borderColor: config.color, color: config.color }}
                              >
                                <Icon className="w-3 h-3" />
                                {config.text}
                              </button>
                            </div>
                            <p className="text-[12px] leading-relaxed line-clamp-3 text-gray-400">
                              {action.prompt || action.description || t('chatPanel.noDescription')}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Streaming */}
            {streaming && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                  <SparklesIcon className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-bg-elevated rounded-xl px-3 py-2.5 max-w-[85%]">
                  {streamingContent ? (
                    <p className="text-[13px] text-gray-200 leading-relaxed whitespace-pre-wrap break-words">{streamingContent}</p>
                  ) : (
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  )}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="flex items-end gap-3 p-4 border-t border-border-subtle flex-shrink-0">
        <div className="flex-1 rounded-lg bg-bg-elevated">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={handleKeyDown}
            placeholder={t('chatPanel.inputPlaceholder')}
            disabled={streaming}
            rows={1}
            className="w-full px-3.5 py-2.5 bg-transparent text-[13px] text-gray-50 placeholder-gray-400 outline-none disabled:opacity-50 resize-none overflow-y-auto"
          />
        </div>
        <button
          onClick={handleSend}
          disabled={!input.trim() || streaming}
          className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
        >
          <PaperAirplaneIcon className="w-[18px] h-[18px] text-white" />
        </button>
      </div>
    </div>
  );
}
